import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Link,
  Alert,
  Container,
  Stack,
  InputAdornment,
  Divider,
  Dialog,
  DialogContent,
  DialogActions,
} from "@mui/material";
import GuestNavbar from "./GuestNavbar";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiClient } from "../../globals/utils/api";
import EmailOutlined from "@mui/icons-material/EmailOutlined";
import ArrowForward from "@mui/icons-material/ArrowForward";
import Login from "@mui/icons-material/Login";
import CalculatorIcon from "@mui/icons-material/Calculate";
import EmailIcon from "@mui/icons-material/Email";
import PremiumCalculator from "./PremiumCalculatorPublic";
import SupportContactDialog from "../../components/SupportContactDialog";

const GuestRequestSample = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [checking, setChecking] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [emailExists, setEmailExists] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [openCalculator, setOpenCalculator] = useState(false);
  const [openSupport, setOpenSupport] = useState(false);

  useEffect(() => {
    localStorage.setItem('selectedState', JSON.stringify({"value":"FL","label":"Florida","id":6}));

    const source = searchParams.get("source");
    const referredBy = searchParams.get("referred_by");
    if (source || referredBy) {
      localStorage.setItem("guestReferralData", JSON.stringify({
        referral_source: source || "",
        referred_by: referredBy || "",
      }));
    } else {
      localStorage.removeItem("guestReferralData");
    }
  }, [searchParams]);

  const onVerifyEmail = async () => {
    setErrorMsg("");
    setEmailExists(false);
    const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
    if (!valid) {
      setErrorMsg("Please enter a valid email address.");
      return;
    }
    setChecking(true);
    try {
      // If the API returns success, it means email does NOT exist → allow guest flow
      await apiClient("/email-exists", "POST", { email: email.trim() });
      setEmailVerified(true);
    } catch (e) {
      // If API throws with detail "Email already exists", block and show login prompt
      if (e?.detail === "Email already exists") {
        setEmailExists(true);
        setEmailVerified(false);
      } else {
        setErrorMsg("We could not verify the email. Please try again.");
      }
    } finally {
      setChecking(false);
    }
  };

  const bondOptions = [
    { title: "Probate Bond", path: "/vermost-probate-bond-request-guest", description: "Start a Probate bond request." },
    { title: "Guardianship Bond", path: "/vermost-guardianship-bond-request-guest", description: "Start a Guardianship bond request." },
    { title: "Trustee Bond", path: "/vermost-trustee-bond-request-guest", description: "Start a Trustee bond request." },
    { title: "VA Bond", path: "/vermost-va-bond-request-guest", description: "Start a VA bond request." },
    { title: "Blanket Bond", path: "/vermost-blanket-bond-request-guest", description: "Start a Blanket bond request." },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", backgroundColor: "#f5f7fb", pb: 10 }}>
      <GuestNavbar />
      <Container maxWidth="lg" sx={{ flex: 1, py: { xs: 3, md: 5 } }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 800, color: "#0f172a", letterSpacing: -0.3 }}>
            Request a Bond as Guest
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary" }}>
            Start by verifying your email. If you already have an account, we’ll guide you to sign in.
          </Typography>
        </Box>

        {!emailVerified ? (
          <Paper
            elevation={0}
            sx={{
              p: 0,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              background: "#ffffff",
              boxShadow: "0 10px 28px rgba(15,23,42,0.06)",
              maxWidth: 720,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                px: { xs: 2, md: 3 },
                py: 2,
                background:
                  "linear-gradient(135deg, rgba(34,197,94,0.15) 0%, rgba(59,130,246,0.12) 100%)",
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <EmailOutlined sx={{ color: "#0f172a" }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#0f172a" }}>
                  Verify your primary email
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                We’ll check if you already have an account associated with this email.
              </Typography>
            </Box>
            <Stack spacing={2} sx={{ p: { xs: 2, md: 3 } }}>
              <TextField
                label="Primary Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !checking) onVerifyEmail();
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailOutlined sx={{ color: "text.disabled" }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Typography variant="caption" sx={{ color: "text.secondary", mt: -1 }}>
                We’ll never share your email. Used only to identify or create your account.
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={onVerifyEmail}
                  disabled={checking}
                  endIcon={
                    checking ? <CircularProgress size={18} color="inherit" /> : <ArrowForward />
                  }
                  sx={{
                    px: 3,
                    borderRadius: 2.5,
                    textTransform: "none",
                    fontWeight: 700,
                    boxShadow: "0 6px 16px rgba(34,197,94,0.25)",
                  }}
                >
                  {checking ? "Verifying..." : "Continue"}
                </Button>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  We’ll check if your email already exists in our system.
                </Typography>
              </Box>

              {errorMsg && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {errorMsg}
                </Alert>
              )}

              {emailExists && (
                <Alert
                  severity="info"
                  icon={<Login fontSize="small" />}
                  sx={{ mt: 1 }}
                  action={
                    <Button color="inherit" size="small" onClick={() => navigate("/")}>
                      Go to Login
                    </Button>
                  }
                >
                  We found an account for this email. Please sign in to continue. If you don’t remember
                  your password, use “Forgot Password” on the sign‑in page. Need help? Contact{" "}
                  <Link href="mailto:support@probatebondpros.com" underline="always">
                    support@probatebondpros.com
                  </Link>
                  .
                </Alert>
              )}
            </Stack>
          </Paper>
        ) : (
          <>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#0f172a", mb: 1.5 }}>
              Choose a bond type
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
              }}
            >
              {bondOptions.map((item) => (
                <Paper
                  key={item.title}
                  elevation={0}
                  onClick={() => navigate(item.path)}
                  sx={{
                    p: 2.5,
                    cursor: "pointer",
                    backgroundColor: "#fff",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 3,
                    transition: "all 0.25s ease",
                    boxShadow: "0 4px 16px rgba(15,23,42,0.04)",
                    "&:hover": {
                      borderColor: "#0f172a",
                      transform: "translateY(-3px)",
                      boxShadow: "0 12px 30px rgba(15,23,42,0.10)",
                    },
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", mb: 0.5 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {item.description}
                  </Typography>
                </Paper>
              ))}
            </Box>
          </>
        )}
      </Container>

      {/* Bottom Tabs */}
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          pr: 2,
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        {/* Support Tab */}
        <Box
          onClick={() => setOpenSupport(true)}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            backgroundColor: "#D3D3D3",
            color: "#333",
            px: 4,
            py: 2,
            borderRadius: "8px 8px 0 0",
            cursor: "pointer",
            minWidth: "300px",
            maxWidth: "400px",
            transition: "all 0.3s ease",
            "&:hover": {
              backgroundColor: "#C8C8C8",
              transform: "translateY(-2px)",
              boxShadow: "0 -6px 16px rgba(0, 0, 0, 0.2)",
            },
            "@media (max-width: 1024px)": {
              minWidth: "260px",
              maxWidth: "340px",
              px: 3,
              py: 1.5,
              gap: 1.5,
            },
            "@media (max-width: 768px)": {
              minWidth: "220px",
              maxWidth: "300px",
              px: 2,
              py: 1,
              gap: 1,
            },
          }}
        >
          <Box
            sx={{
              width: 20,
              height: 20,
              backgroundColor: "#333",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "2px",
              flexShrink: 0,
              "@media (max-width: 1024px)": {
                width: 18,
                height: 18,
              },
              "@media (max-width: 768px)": {
                width: 16,
                height: 16,
              },
            }}
          >
            <EmailIcon
              sx={{
                fontSize: 14,
                color: "white",
                "@media (max-width: 1024px)": {
                  fontSize: 12,
                },
                "@media (max-width: 768px)": {
                  fontSize: 10,
                },
              }}
            />
          </Box>
          <Typography
            variant="body1"
            sx={{
              fontSize: "1rem",
              fontWeight: 500,
              color: "#333",
              whiteSpace: "nowrap",
              "@media (max-width: 1024px)": {
                fontSize: "0.9rem",
              },
              "@media (max-width: 768px)": {
                fontSize: "0.8rem",
              },
            }}
          >
            Contact Support
          </Typography>
        </Box>

        {/* Calculator Tab */}
        <Box
          onClick={() => setOpenCalculator(true)}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            backgroundColor: "#D3D3D3",
            color: "#333",
            px: 4,
            py: 2,
            borderRadius: "8px 8px 0 0",
            cursor: "pointer",
            minWidth: "320px",
            maxWidth: "400px",
            transition: "all 0.3s ease",
            "&:hover": {
              backgroundColor: "#C8C8C8",
              transform: "translateY(-2px)",
              boxShadow: "0 -6px 16px rgba(0, 0, 0, 0.2)",
            },
            "@media (max-width: 1024px)": {
              minWidth: "280px",
              maxWidth: "350px",
              px: 3,
              py: 1.5,
              gap: 1.5,
            },
            "@media (max-width: 768px)": {
              minWidth: "240px",
              maxWidth: "300px",
              px: 2,
              py: 1,
              gap: 1,
            },
          }}
        >
          <Box
            sx={{
              width: 20,
              height: 20,
              backgroundColor: "#333",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "2px",
              flexShrink: 0,
              "@media (max-width: 1024px)": {
                width: 18,
                height: 18,
              },
              "@media (max-width: 768px)": {
                width: 16,
                height: 16,
              },
            }}
          >
            <CalculatorIcon
              sx={{
                fontSize: 14,
                color: "white",
                "@media (max-width: 1024px)": {
                  fontSize: 12,
                },
                "@media (max-width: 768px)": {
                  fontSize: 10,
                },
              }}
            />
          </Box>
          <Typography
            variant="body1"
            sx={{
              fontSize: "1rem",
              fontWeight: 500,
              color: "#333",
              whiteSpace: "nowrap",
              "@media (max-width: 1024px)": {
                fontSize: "0.9rem",
              },
              "@media (max-width: 768px)": {
                fontSize: "0.8rem",
              },
            }}
          >
            Use our Bond Premium Calculator
          </Typography>
        </Box>
      </Box>

      <SupportContactDialog
        open={openSupport}
        onClose={() => setOpenSupport(false)}
        context="Guest Request"
      />

      <Dialog
        open={openCalculator}
        onClose={() => setOpenCalculator(false)}
        sx={{ "& .MuiPaper-root": { borderRadius: "20px", maxWidth: "90vw" } }}
      >
        <DialogContent>
          <PremiumCalculator />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenCalculator(false)}
            sx={{
              borderRadius: "12px",
              color: "#333333",
              fontWeight: 600,
              "&:hover": { backgroundColor: "rgba(51, 51, 51, 0.08)" },
              textTransform: "none",
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GuestRequestSample;